import React, { useCallback, useRef, useState } from 'react';

import {
  Button,
  TextSearchReplace,
  FormItem,
  SubmitButton,
  Dropdown,
} from '../../../../components';
import { Form } from '../../../../components/Form';
import { Input } from '../../../../components/Input';
import { Modal } from '../../../../components/Modal';
import { DnDTip } from '../../../../components/TextSearchReplace/components';
import { validateJSONforInput } from '../../../../utilities/validateJSONforInput';

type ModelFieldsFormValues = {
  modelName: string | null;
  modelSettings: string;
};

type PromptEditorModalProps = {
  initialContent: string;
  initialModelName: string | null;
  initialModelSettings: string;
  modelOptions: OptionType<string>[];
  hideModelFields?: boolean;
  onClose: () => void;
  onSave: (
    content: string,
    modelName: string | null,
    modelSettings: string
  ) => void;
};

export const PromptEditorModal: React.FC<PromptEditorModalProps> = ({
  initialContent,
  initialModelName,
  initialModelSettings,
  modelOptions,
  hideModelFields,
  onClose,
  onSave,
}) => {
  const editorRef = useRef<HTMLDivElement>(
    null
  ) as React.RefObject<HTMLDivElement>;

  const [formValues, setFormValues] = useState<ModelFieldsFormValues>({
    modelName: initialModelName,
    modelSettings: initialModelSettings,
  });

  const handleSave = useCallback(() => {
    const content = editorRef.current
      ? editorRef.current.innerText
      : initialContent;
    onSave(content, formValues.modelName, formValues.modelSettings);
    onClose();
  }, [onSave, onClose, formValues, initialContent]);

  const footer = (
    <div className="flex items-center w-full justify-between gap-x-4">
      <DnDTip />
      <div className="flex gap-x-4">
        <Button
          size="medium"
          type="contrast"
          label="Cancel"
          className="text-main flex-1 min-w-0"
          onClick={onClose}
        />
        <SubmitButton
          size="medium"
          label="Save"
          className="flex-1 min-w-0"
          onClick={handleSave}
        />
      </div>
    </div>
  );

  return (
    <Form initialValue={formValues} onChange={setFormValues}>
      <Modal
        title="Edit Content"
        size="big"
        open
        onClose={onClose}
        footer={footer}
      >
        <div className="flex flex-col gap-sm h-full">
          <TextSearchReplace
            editorRef={editorRef}
            initialContent={initialContent}
            isLibraryMode
          />

          {!hideModelFields && (
            <div className="flex flex-col gap-y-md pt-2">
              <FormItem<ModelFieldsFormValues>
                name="modelName"
                label="LLM Model"
                inline={false}
              >
                <Dropdown<string>
                  options={modelOptions}
                  placeholder="Select LLM model"
                />
              </FormItem>
              <div className="mb-10">
                <FormItem<ModelFieldsFormValues>
                  name="modelSettings"
                  label="Model Settings"
                  inline={false}
                  validate={validateJSONforInput}
                  errorBelowInput
                >
                  <Input
                    multiline
                    placeholder='{"key": "value"}'
                    inputClassName="font-mono text-mainText text-medium"
                  />
                </FormItem>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </Form>
  );
};
